import { cloneDeep } from "lodash";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import ActionDialog from "../../components/actionDialog";
import MainPageDialog from "../../components/dialog";
import { PatientSearchField } from "../../components/reactHookFormFields/patientSearchField";
import { ReactSelectField } from "../../components/reactHookFormFields/selectField";
import { useGroupChatContext } from "../../context/groupchatContext";
import { getBaseUrl } from "../../helpers/utility";
import { useSendMessageMutation } from "../../services/chatService";
import { useGetVezoDisclaimerQuery, useGetVezoRoomsQuery, useSaveSessionMutation, vezoSessionsApi } from "../../services/vezoService";


export const InstantSessionDialog = ({closeHandler}) => {

    const [patientContact, setPatientContact] = useState([]);
    const [disclaimerPopupData, setDisclaimerPopupData] = useState({open: false, title: ''})

    const groupChatContext = useGroupChatContext();
    const { authData, setVezoCallData, selectedChat } = useMemo(() => ({
        authData: groupChatContext.authData,
        setVezoCallData: groupChatContext.setVezoCallData,
        selectedChat: groupChatContext.selectedChat
    }));

    const { data: vezoAllRooms } = useGetVezoRoomsQuery();
    const { data: vezoDisclaimer } = useGetVezoDisclaimerQuery();
    const [saveSession] = useSaveSessionMutation();
    const [sendMessage] = useSendMessageMutation();
    const [getPatientContact] = vezoSessionsApi.endpoints.getPatientContact.useLazyQuery();

    const {
        handleSubmit,
        control,
        setValue,
        getValues,
        formState: { errors },
    } = useForm({defaultValues: { name: {pid: selectedChat.pid, full_name: selectedChat.patient?.name, label: selectedChat.patient?.name} }});
    
    const footerList = [
        {
            title: 'Start',
            type: "save",
            action: () => {
                handleSubmit(onSubmit)();
            }
        },
        {
            title: "Cancel",
            type: "cancel",
            action: () => {
                closeHandler();
            }
        }
    ];

    useEffect(() => {
        if(vezoAllRooms && vezoAllRooms.length > 0){
            let roomIndexToUse = vezoAllRooms?.findIndex((check) => (check?.user_role=="owner"));
            if(roomIndexToUse < 0){
                setValue("current_room", {...vezoAllRooms[0], label: vezoAllRooms[0].room_name});
            }
            else {
                setValue("current_room", {...vezoAllRooms[roomIndexToUse], label: vezoAllRooms[roomIndexToUse].room_name});
            }
        }
    }, [vezoAllRooms]);

    const onChangePatient = (val) => {
        if(val){
            getPatientContact({pid: val.pid}).then((res)=>{
                if(res.data){
                    setPatientContact(res.data)
                }
            })
        }
        else{
            setPatientContact([])
        }
    }

    const showDisclaimer = () => {
        const disclaimerData = {
            open: true,
            title: vezoDisclaimer[0]?.TermsCondition?.text,
            callBackFun: () => saveSessionInfo()
        };
        setDisclaimerPopupData(disclaimerData)
    };


    const onSubmit = () => {
        showDisclaimer()
    }

    const startVezoCall = (item) =>{
        const params = {
            show: true,
            url: `${getBaseUrl().telemed_url}?mid=${item?.visit_key}&token=${authData.auth}&parent=provider_app&tz=${new Date().getTimezoneOffset()}&practice_id=${authData.practice?.id}&consent_signed=1`,
            last_call: cloneDeep(item)
        }
        setVezoCallData(params);
    }

    const saveSessionInfo = async() => {
        const values = getValues();

        const tempPatContacts = patientContact.map((item)=>{
            return {...item, cell_checked: true, email_checked: true}
        })

        const sessionPayload = {
            timezone_long: Intl.DateTimeFormat().resolvedOptions().timeZone,
            duration: 15,
            datetime: moment().format('YYYY-MM-DD HH:mm:ss'),
            encounter_type: "telemedicine",
            encounter_type_temp: "telemedicine",
            patient_contacts: tempPatContacts,
            pid: values.name?.pid,
            vezo_id: values.current_room?.vezo_id,
            title: values.name?.full_name+' '+moment().format('MM/DD/YYYY hh:mm A'),
            title_changed: "patientSeleted",
            user_id: authData.id,
            user_name: authData.name,
        }

        const sessionResponse = await saveSession(sessionPayload);

        if(sessionResponse.data){
            const payload = {pid: selectedChat?.pid, location_id: selectedChat?.location?.id || selectedChat?.location_id, message:{type: 'event', event_type: 'vezo_session', data: {start_time: sessionPayload.datetime, user_id: authData.id, status:'', provider_name: authData.name, vezo_id: sessionResponse.data.vezo_id, visit_url: sessionResponse.data.visit_url, visit_key: sessionResponse.data.visit_key}}};
            sendMessage(payload);
            toast.success("Appointment created successfully!");
            closeHandler();
            startVezoCall(sessionResponse.data)
        }
    }

    return (
        <>
            <MainPageDialog
                title="Instant Session"
                footer={true}
                footerList={footerList}
                modalSize="md"
                onCancelHandler={() => closeHandler()}
            >
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="flex gap-4">
                        <ReactSelectField
                            title="Selected Room"
                            name="current_room"
                            className="text-start"
                            control={control}
                            optionData={vezoAllRooms?.map((v, k) => ({
                                ...v,
                                label: v.room_name,
                            }))}
                            errors={errors}
                            rules={{
                                required: {
                                    value: true,
                                },
                            }}
                        />
                        <PatientSearchField
                            title="Patient Name"
                            name="name"
                            control={control}
                            onChange={onChangePatient}
                        />
                    </div>
                    <p className="py-2">This will start a session instantly and you will be able to add guests from a running session.</p>
                </form>
            </MainPageDialog>

            <ActionDialog
                headerTitle="Disclaimer!"
                onCancelHandler={() => setDisclaimerPopupData({ open: false })}
                onConfirmHandler={() => {
                    disclaimerPopupData.callBackFun();
                    setDisclaimerPopupData({ open: false });
                }}
                confirmButtonText="Sign"
                showModal={disclaimerPopupData.open}
                title={disclaimerPopupData?.title}
                setShowModal={() => setDisclaimerPopupData({ open: false })}
            />
        </>
    );
};
